import type {DocumentObject} from 'jsonapi-fractal';
import {BaseApi, type RequestInit} from 'toolbox';
import Auth from "@/auth";

export abstract class Api extends BaseApi {
    protected baseUrl = `${import.meta.env.VITE_APP_API_URL}`;
    protected authToken = window.sessionStorage.getItem('access_token') || '';

    async processResponse(response: any) {
        const succeeded = response.ok || response.statusText?.toLowerCase() === 'ok';
        let data;

        try {
            data = typeof response.json === 'function' ? await response.json() : response.data;
        }
        catch (error) {
            if (succeeded) {
                return undefined;
            }

            throw error;
        }

        if (succeeded) {
            return data;
        }
        else if (response.status === 401 && data?.message?.toLowerCase().includes('unauthenticated')) {
            Auth.logout('session_expired', window.location.href);
        }
        else if (response.status === 500) {
            return Promise.reject('Server Error');
        }
        else {
            let error = data && (data.message || data);

            try {
                if (error.errors?.length && error.errors[0].message && JSON.parse(error.errors[0].message).hasOwnProperty('errors')) {
                    const errors = JSON.parse(error.errors[0].message);
                    if (errors.errors.length && !errors.errors[0].hasOwnProperty('key') && errors.errors[0].hasOwnProperty('detail')) {
                        error = errors.errors[0].detail;
                    }
                    else {
                        error = errors;
                    }
                }
                else if (JSON.parse(error).hasOwnProperty('errors')) {
                    error = JSON.parse(error);
                }
            }
            catch (err) {
                // Plain-text API messages are already suitable for display. Only
                // replace an empty or unparseable payload with the generic fallback.
                error = error || 'Error Occurred';
            }

            return Promise.reject(error);
        }
    }

    protected request(input: RequestInit): Promise<Response> {
        input.headers = {
            ...input.headers,
            'uuid': window.sessionStorage.getItem('uuid') || '',
        };
        return super.request(input);
    }
}

export type ApiOptions = {
    include: string[],
}

export type LinksObject = {
    self: string;
    first: string;
    last: string;
};

export type MetaObject = {
    pagination: {
        total: number;
        count: number;
        per_page: number;
        current_page: number;
        total_pages: number;
    };
};

export type APIResponse = DocumentObject & {
    links: LinksObject;
    meta: MetaObject;
};

export type ObjectWithMeta<T> = {
    data: T | T[];
    meta: MetaObject;
    links: LinksObject;
};
